/**
 * Contact Engagement Detail Panel
 *
 * Expandable panel showing email event history for a contact.
 * Lazy-loads events when the row is expanded.
 */
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import { useContactEvents } from "../../hooks/admin/use-analytics";

interface ContactEngagementPanelProps {
  contactId: string;
}

const EVENT_LABELS: Record<
  string,
  { label: string; color: "success" | "info" | "warning" | "error" | "default" }
> = {
  delivered: { label: "Delivered", color: "success" },
  opened: { label: "Opened", color: "info" },
  clicked: { label: "Clicked", color: "success" },
  bounced: { label: "Bounced", color: "error" },
  complained: { label: "Spam Report", color: "error" },
  unsubscribed: { label: "Unsubscribed", color: "warning" },
};

function formatEventDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-NZ", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateUrl(url: string, maxLength: number = 60): string {
  if (url.length <= maxLength) return url;
  return url.substring(0, maxLength) + "...";
}

export function ContactEngagementPanel({
  contactId,
}: ContactEngagementPanelProps) {
  const { data, isLoading, error } = useContactEvents(contactId);

  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Failed to load engagement data
        </Typography>
      </Box>
    );
  }

  if (!data?.events.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No email events recorded for this contact yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom color="text.secondary">
        Recent Email Events
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {data.events.map((event) => {
          const config = EVENT_LABELS[event.eventType] ?? {
            label: event.eventType,
            color: "default" as const,
          };
          return (
            <Box
              key={event.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 0.5,
              }}
            >
              <Chip
                label={config.label}
                size="small"
                color={config.color}
                variant="outlined"
                sx={{ minWidth: 90 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 130 }}
              >
                {formatEventDate(event.timestamp)}
              </Typography>
              {event.url && (
                <Link
                  href={event.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {truncateUrl(event.url)}
                </Link>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
